import type { PlaceType } from '../types';
import './Filters.css';
import { useT } from '../i18n';

/** What the user has narrowed the list to. */
export interface FilterState {
    query: string;
    types: Set<PlaceType>;
    /** Hide anything no certifier stands behind. */
    certifiedOnly: boolean;
    /** Hide lapsed and withdrawn certificates. */
    hideExpired: boolean;
    nearMe: boolean;
}

export const EMPTY_FILTERS: FilterState = {
    query: '',
    types: new Set(),
    certifiedOnly: false,
    hideExpired: false,
    nearMe: false,
};

const TYPE_GROUPS: { key: 'filter.eat' | 'filter.shop' | 'filter.pray'; types: PlaceType[] }[] = [
    { key: 'filter.eat', types: ['restaurant', 'fast_food', 'cafe', 'food_court', 'hotel_restaurant', 'attraction_dining'] },
    { key: 'filter.shop', types: ['grocery', 'butcher', 'bakery'] },
    { key: 'filter.pray', types: ['mosque', 'prayer_room'] },
];

interface Props {
    value: FilterState;
    onChange: (next: FilterState) => void;
    counts: { certified: number; expired: number; nearby: number };
    hasLocation: boolean;
}

/**
 * Filters, as a single scrolling row of chips.
 *
 * v2 had none at all, which for 500+ Paris results made the list unusable. The
 * order is deliberate: what kind of place first (the most common need), then the
 * two trust filters that are this product's reason to exist.
 */
export function Filters({ value, onChange, counts, hasLocation }: Props) {
    const { t } = useT();
    const toggleGroup = (types: PlaceType[]) => {
        const next = new Set(value.types);
        const allOn = types.every((t) => next.has(t));
        for (const t of types) {
            if (allOn) next.delete(t);
            else next.add(t);
        }
        onChange({ ...value, types: next });
    };

    const active = (types: PlaceType[]) => types.every((t) => value.types.has(t));
    const anyActive = value.types.size > 0 || value.certifiedOnly || value.hideExpired || value.nearMe;

    return (
        <div className="filters" role="group" aria-label={t('filter.label')}>
            <div className="filters__scroll">
                {TYPE_GROUPS.map((g) => (
                    <button
                        key={g.key}
                        type="button"
                        className={`chip ${active(g.types) ? 'is-on' : ''}`}
                        aria-pressed={active(g.types)}
                        onClick={() => toggleGroup(g.types)}
                    >
                        {t(g.key)}
                    </button>
                ))}

                <span className="filters__divider" aria-hidden="true" />

                <button
                    type="button"
                    className={`chip chip--trust ${value.certifiedOnly ? 'is-on' : ''}`}
                    aria-pressed={value.certifiedOnly}
                    onClick={() => onChange({ ...value, certifiedOnly: !value.certifiedOnly })}
                    disabled={counts.certified === 0}
                    title={counts.certified === 0 ? t('filter.noneCertified') : undefined}
                >
                    {t('filter.certifiedOnly')}
                    <span className="chip__count">{counts.certified}</span>
                </button>

                <button
                    type="button"
                    className={`chip chip--trust ${value.hideExpired ? 'is-on' : ''}`}
                    aria-pressed={value.hideExpired}
                    onClick={() => onChange({ ...value, hideExpired: !value.hideExpired })}
                    disabled={counts.expired === 0}
                    title={counts.expired === 0 ? t('filter.noneExpired') : undefined}
                >
                    {t('filter.hideExpired')}
                    <span className="chip__count">{counts.expired}</span>
                </button>

                {hasLocation && (
                    <button
                        type="button"
                        className={`chip ${value.nearMe ? 'is-on' : ''}`}
                        aria-pressed={value.nearMe}
                        onClick={() => onChange({ ...value, nearMe: !value.nearMe })}
                    >
                        {t('filter.nearMe')}
                        <span className="chip__count">{counts.nearby}</span>
                    </button>
                )}

                {anyActive && (
                    <button type="button" className="chip chip--clear" onClick={() => onChange({ ...EMPTY_FILTERS, query: value.query })}>
                        {t('filter.clear')}
                    </button>
                )}
            </div>
        </div>
    );
}
